import styled from '@emotion/styled'
import { ExtranetTicketDto, ExtranetTicketStatusEnum } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  formatDateTimeString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacH6,
  PharmacIcon,
  PharmacIconProps,
  PharmacLoadingDots,
  PharmacText,
} from '@pharmac/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PropsWithChildren, useState } from 'react'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'
import { ConfirmationModal } from '../confirmation-modal/ConfirmationModal'
import { ExtranetParagraph } from '../extranet/ExtranetParagraph'
import { TicketAttachments } from './TicketAttachments'
import { TicketComments } from './TicketComments'
import { useTicket } from './useTicket'

type OpenTicketsListItemDetailsProps = {
  ticketId: string
}

const TicketBadgeEl = styled.div`
  display: flex;
  background-color: var(--color-accent-2);
  border-radius: 8px;
  margin-right: var(--spacing-small);
  margin-bottom: var(--spacing-small);

  justify-content: center;
  align-items: center;
  padding: 2px var(--spacing-small);
  border: 1px solid var(--color-accent-4);
  svg {
    margin-left: var(--spacing-small);
    transform: translateY(var(--spacing-tiny));
    color: var(--color-success);
  }
`

const TicketBadge = ({
  icon,
  children,
}: PropsWithChildren & { icon: PharmacIconProps['icon'] }) => {
  return (
    <TicketBadgeEl>
      <div>{children}</div>
      <div>
        <PharmacIcon icon={icon} />
      </div>
    </TicketBadgeEl>
  )
}

const TicketBadges = ({
  ticket,
  flexColumn,
}: {
  ticket: ExtranetTicketDto
  flexColumn?: boolean
}) => {
  const { t } = useLanguage()
  return (
    <ExtranetContainer
      full
      flexRow={!flexColumn}
      flexColumn={flexColumn}
      alignCenter={flexColumn}
      justifyStart={!flexColumn}
      marginMediumBottom={ticket.isPrivate || ticket.isConfidential}
    >
      {ticket.isPrivate && (
        <TicketBadge icon="check">{t('contact.privateTicketButton')}</TicketBadge>
      )}
      {ticket.isConfidential && (
        <TicketBadge icon="check">{t('contact.confidentialTicketButton')}</TicketBadge>
      )}
    </ExtranetContainer>
  )
}

export const TicketsListItemDetails = ({ ticketId }: OpenTicketsListItemDetailsProps) => {
  const { t } = useLanguage()
  const [cancelConversation, setCancelConversation] = useState<boolean>(false)
  const [closeConversation, setCloseConversation] = useState<boolean>(false)
  const { minDesktop, minTablet } = useBreakpoint()
  const { user, isPharmacCustomerService } = useAuthenticationContext()
  const { customer } = useCustomerContext()

  const queryClient = useQueryClient()

  const { ticket, ticketIsLoading, attachments } = useTicket(ticketId)

  const isTicketClosed =
    ticket?.status &&
    [
      ExtranetTicketStatusEnum.Value08CancelledByCustomer,
      ExtranetTicketStatusEnum.Value06Resolved,
      ExtranetTicketStatusEnum.Value07Closed,
    ].includes(ticket.status)

  const isTicketResolved =
    ticket?.status && ticket.status === ExtranetTicketStatusEnum.Value06Resolved

  const { mutate: closeTicket } = useMutation({
    mutationFn: () => api.tickets.closeTicket(customer!.id, ticketId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['__getTickets', customer?.id],
      })

      setCloseConversation(false)
      window.location.reload()
    },
  })

  const { mutate: cancelTicket } = useMutation({
    mutationFn: () => api.tickets.cancelTicket(customer!.id, ticketId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['__getTickets', customer!.id],
      })

      setCancelConversation(false)
      window.location.reload()
    },
  })

  const { mutate: reopenTicket } = useMutation({
    mutationFn: () => api.tickets.reopenTicket(customer!.id, ticketId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['__getTickets', customer!.id],
      })
      window.location.reload()
    },
  })

  if (!ticket || ticketIsLoading) {
    return (
      <ExtranetContainer
        style={{ minHeight: '100px' }}
        full
        flexRow
        justifyCenter
        alignCenter
      >
        <PharmacLoadingDots />
      </ExtranetContainer>
    )
  }

  const parseIncidentId = (name: string) => {
    return name.split('-').slice(1, 2)
  }

  return (
    <>
      <ExtranetContainer
        flexColumn
        full
        paddingMediumVertical
        paddingLargeHorizontal={minDesktop}
        paddingMediumHorizontal={!minDesktop}
        style={{ borderTop: '2px solid var(--color-accent-4)' }}
      >
        {!minDesktop && (
          <>
            <TicketBadges
              ticket={ticket}
              flexColumn={!minTablet}
            />
            <ExtranetContainer
              flexColumn
              full
            >
              <PharmacH6>ID</PharmacH6>
              <PharmacText paddingMediumVertical>{parseIncidentId(ticket.incidentId)}</PharmacText>
            </ExtranetContainer>

            <ExtranetContainer
              flexColumn
              full
            >
              <PharmacH6>{t('extranet:contact.createdAt')}</PharmacH6>
              <PharmacText paddingMediumVertical>
                {formatDateTimeString(ticket.created)}
              </PharmacText>
            </ExtranetContainer>

            <ExtranetContainer
              flexColumn
              full
            >
              <PharmacH6>{t('extranet:contact.updatedAt')}</PharmacH6>
              <PharmacText paddingMediumVertical>
                {formatDateTimeString(ticket.updated)}
              </PharmacText>
            </ExtranetContainer>

            <ExtranetContainer
              flexColumn
              full
            >
              <PharmacH6>{t('extranet:contact.writer')}</PharmacH6>
              <PharmacText paddingMediumVertical>{ticket.extranetUserName}</PharmacText>
            </ExtranetContainer>
          </>
        )}
        {minDesktop && <TicketBadges ticket={ticket} />}
        <ExtranetContainer
          flexColumn
          full
          style={{ overflowWrap: 'anywhere' }}
        >
          <PharmacH6>{t('extranet:contact.subject')}</PharmacH6>
          <div style={{ whiteSpace: 'pre-line' }}>
            <ExtranetParagraph>{ticket.subject}</ExtranetParagraph>
          </div>
        </ExtranetContainer>
        <ExtranetContainer
          flexColumn
          full
          style={{ overflowWrap: 'anywhere' }}
        >
          <PharmacH6>{t('extranet:contact.description')}</PharmacH6>
          <div style={{ whiteSpace: 'pre-line' }}>
            <ExtranetParagraph>{ticket.description}</ExtranetParagraph>
          </div>
        </ExtranetContainer>

        {attachments && attachments.length > 0 && (
          <TicketAttachments
            items={attachments}
            ticketId={ticket.id}
          />
        )}

        <TicketComments ticketId={ticketId} />
      </ExtranetContainer>
      <ExtranetContainer
        full
        flexRow
        style={{ borderTop: '2px solid var(--color-accent-4)' }}
        paddingLargeVertical
        marginLargeTop
        paddingLargeHorizontal
      >
        <ExtranetContainer
          full
          flexRow={minDesktop}
          flexColumn={!minDesktop}
          alignCenter
          justifyEnd={minDesktop}
        >
          {isTicketClosed && minDesktop && (
            <ExtranetContainer
              half
              flexRow
            >
              <PharmacButton
                variant={!isTicketResolved ? 'secondary' : 'primary'}
                disabled={!isTicketResolved || isPharmacCustomerService}
                marginMediumRight
                onClick={() => reopenTicket()}
              >
                <ExtranetContainer
                  flexRow
                  justifyCenter
                  alignCenter
                >
                  <PharmacIcon
                    size="16"
                    icon="unlock"
                  />
                  <PharmacText marginSmallHorizontal>
                    {t('extranet:contact.reopenTicket')}
                  </PharmacText>
                </ExtranetContainer>
              </PharmacButton>
            </ExtranetContainer>
          )}
          <ExtranetContainer
            half={minDesktop}
            full={!minDesktop}
            flexRow={minDesktop}
            flexColumn={!minDesktop}
            justifyEnd={minDesktop}
            alignCenter={!minDesktop}
          >
            <PharmacButton
              fullWidth={!minDesktop}
              marginMediumBottom={!minDesktop}
              variant="secondary"
              marginMediumRight={minDesktop}
              disabled={
                isTicketClosed ||
                ticket.extranetUserId !== user?.keycloakId ||
                isPharmacCustomerService
              }
              onClick={() => setCancelConversation(true)}
            >
              <ExtranetContainer
                flexRow
                justifyCenter
                alignCenter
              >
                <PharmacIcon
                  size="16"
                  icon="x"
                />
                <PharmacText
                  marginSmallHorizontal
                  weight="semibold"
                >
                  {t('extranet:contact.cancelTicket')}
                </PharmacText>
              </ExtranetContainer>
            </PharmacButton>
            <PharmacButton
              fullWidth={!minDesktop}
              marginMediumBottom={!minDesktop}
              onClick={() => setCloseConversation(true)}
              disabled={isTicketClosed || isPharmacCustomerService}
              variant={isTicketClosed ? 'secondary' : 'primary'}
            >
              <ExtranetContainer
                flexRow
                justifyCenter
                alignCenter
              >
                <PharmacIcon
                  size="16"
                  icon="check"
                />
                <PharmacText marginSmallHorizontal>{t('extranet:contact.closeTicket')}</PharmacText>
              </ExtranetContainer>
            </PharmacButton>
          </ExtranetContainer>
          {isTicketClosed && !minDesktop && (
            <PharmacButton
              fullWidth
              variant={!isTicketResolved ? 'secondary' : 'primary'}
              disabled={!isTicketResolved || isPharmacCustomerService}
              onClick={() => reopenTicket()}
            >
              <ExtranetContainer
                flexRow
                justifyCenter
                alignCenter
              >
                <PharmacIcon
                  size="16"
                  icon="unlock"
                />
                <PharmacText marginSmallHorizontal>
                  {t('extranet:contact.reopenTicket')}
                </PharmacText>
              </ExtranetContainer>
            </PharmacButton>
          )}
        </ExtranetContainer>
      </ExtranetContainer>
      <ConfirmationModal
        title={t('extranet:contact.cancelTicket')}
        content={
          <ExtranetContainer full>
            <PharmacText>{t('extranet:contact.cancelTicketModal')}</PharmacText>
          </ExtranetContainer>
        }
        onSubmit={() => cancelTicket()}
        onClose={() => setCancelConversation(false)}
        showOnProp={cancelConversation}
      />
      <ConfirmationModal
        title={t('extranet:contact.closeTicket')}
        content={
          <ExtranetContainer full>
            <PharmacText>{t('extranet:contact.closeTicketModal')}</PharmacText>
          </ExtranetContainer>
        }
        onSubmit={() => closeTicket()}
        onClose={() => setCloseConversation(false)}
        showOnProp={closeConversation}
      />
    </>
  )
}
